<template>
  <div class="profile">
    <!-- Skeleton hero until the whitelabel color loads, so the brand color
         never flashes a fallback value -->
    <div v-if="loading" class="profile-hero profile-hero--sk">
      <div class="sk sk-avatar"></div>
      <div style="flex: 1">
        <div class="sk sk-line sk-line-lg"></div>
        <div class="sk sk-line sk-line-sm"></div>
      </div>
    </div>

    <div v-else class="profile-hero" :style="{ background: heroBg }">
      <div class="profile-hero-id">
        <v-avatar size="64" class="profile-avatar">
          <v-img :src="logoLink" contain :key="logoUpdateTimestamp" @error="onImageError" />
        </v-avatar>
        <div>
          <div class="profile-name">{{ displayName }}</div>
          <div class="profile-email">{{ account.email || 'Não informado' }}</div>
        </div>
      </div>
      <v-btn class="profile-edit" flat round @click="showChangeForm">
        <v-icon left small>link</v-icon> Alterar Links
      </v-btn>
    </div>

    <!-- Info grid -->
    <div class="profile-grid">
      <div class="info-tile">
        <v-icon class="info-tile-icon" :color="brandColor">badge</v-icon>
        <div>
          <div class="info-tile-label">Tipo de conta</div>
          <div class="info-tile-value">Conta {{ partnerTypeLabel }}</div>
        </div>
      </div>
      <div class="info-tile">
        <v-icon class="info-tile-icon" :color="brandColor">fingerprint</v-icon>
        <div>
          <div class="info-tile-label">ID da conta</div>
          <div class="info-tile-value">#{{ newAccount.id || 'N/A' }}</div>
        </div>
      </div>
      <div class="info-tile">
        <v-icon class="info-tile-icon" :color="brandColor">account_balance_wallet</v-icon>
        <div>
          <div class="info-tile-label">Saldo disponível</div>
          <div class="info-tile-value">{{ $n(getBalance(), "currency") }}</div>
        </div>
      </div>
      <div class="info-tile">
        <v-icon class="info-tile-icon" color="success">check_circle</v-icon>
        <div>
          <div class="info-tile-label">Status</div>
          <div class="info-tile-value">Conta ativa</div>
        </div>
      </div>
      <div class="info-tile info-tile--wide">
        <v-icon class="info-tile-icon" :color="brandColor">email</v-icon>
        <div>
          <div class="info-tile-label">E-mail principal</div>
          <div class="info-tile-value">{{ account.email || 'Não informado' }}</div>
        </div>
      </div>
      <div v-if="newAccount.styles && newAccount.styles.panel_url" class="info-tile info-tile--wide">
        <v-icon class="info-tile-icon" :color="brandColor">public</v-icon>
        <div>
          <div class="info-tile-label">URL do painel</div>
          <div class="info-tile-value">{{ newAccount.styles.panel_url }}</div>
        </div>
      </div>
    </div>

    <!-- Manage -->
    <div class="profile-section-title">Gerenciar conta</div>
    <div class="profile-actions">
      <button type="button" class="action-card" @click="showChangePasswordForm">
        <v-icon :color="brandColor">lock</v-icon><span>Alterar Senha</span>
      </button>
      <button type="button" class="action-card" @click="showChangeLogoForm">
        <v-icon :color="brandColor">photo_camera</v-icon><span>Alterar Logo</span>
      </button>
      <button type="button" class="action-card" @click="showChangeFaviconForm">
        <v-icon :color="brandColor">image</v-icon><span>Alterar Favicon</span>
      </button>
      <button type="button" class="action-card" @click="showChangeColorForm">
        <v-icon :color="brandColor">palette</v-icon><span>Alterar Cor</span>
      </button>
    </div>

    <div class="profile-section-title">Precisa de ajuda?</div>
    <div class="profile-actions">
      <router-link class="action-card" :to="{ name: 'tickets' }">
        <v-icon :color="brandColor">chat</v-icon><span>Chat Online</span>
      </router-link>
      <button type="button" class="action-card" @click="sendEmail">
        <v-icon :color="brandColor">email</v-icon><span>Enviar E-mail</span>
      </button>
    </div>

    <!-- Modais (mantidos) -->
    <change-data />
    <change-password />
    <change-logo />
    <change-favicon />
    <change-color />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" bottom>
      {{ snackbar.message }}
      <v-btn flat @click="snackbar.show = false">Fechar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { EventBus } from "@/services/event-bus";

export default {
  created() {
    this.$store.dispatch("account/getAccount");
    this.$store.dispatch("system/fetchSystemConfiguration");
    this.$store.dispatch("user/getBalance");

    // Listener para atualização de logo
    this.$root.$on('logo-updated', this.onLogoUpdated);
  },
  mounted() {
    // Verificar Vuetify theme
    if (this.$vuetify && this.$vuetify.theme) {
      // Se a cor do tema Vuetify não corresponde, atualizar
      if (this.$vuetify.theme.primary !== this.color && this.color && this.color !== 'primary') {
        this.$vuetify.theme.primary = this.color;
      }
    }

    // Forçar re-render após um pequeno delay para garantir que os dados foram carregados
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.color && this.color !== 'primary' && this.$vuetify && this.$vuetify.theme) {
          this.$vuetify.theme.primary = this.color;
        }
      }, 1000);
    });
  },
  beforeDestroy() {
    this.$root.$off('logo-updated', this.onLogoUpdated);
  },
  computed: {
    ...mapGetters({
      partner: "account/partner",
      account: "account/account",
      logoLinkFromStore: "account/logoLink",
      systemLogoUrl: "system/logoUrl",
      systemConfiguration: "system/systemConfiguration",
      systemPrimaryColor: "system/primaryColor",
      favicon: "account/favicon",
      accountColor: "account/color",
      showLogoForm: "account/showLogoForm",
      showFaviconForm: "account/showFaviconForm",
      showColorForm: "account/showColorForm",
      showForm: "account/showForm",
      loading: "account/loading",
      balance: "user/balance",
    }),
    color() {
      // Usar cor da conta se existir, senão usar cor primária do sistema
      const finalColor = this.accountColor || this.systemPrimaryColor || 'var(--primary-color, #1976D2)';
      return finalColor;
    },
    // Background aceita hex ou var(); usa a cor do tema diretamente.
    heroBg() {
      return this.color;
    },
    // Cor válida (hex) para props do Vuetify (v-icon), com fallback seguro.
    brandColor() {
      const c = this.accountColor || this.systemPrimaryColor;
      return c && String(c).startsWith("#") ? c : "#1976D2";
    },
    logoLink() {
      // Usar timestamp para garantir reatividade (força re-computation quando logo é atualizada)
      this.logoUpdateTimestamp; // Acessa a variável para criar dependência reativa

      // Priorizar logo da configuração do sistema (reativo às mudanças)
      if (this.systemConfiguration && this.systemConfiguration.logo_url &&
          this.systemConfiguration.logo_url.trim() !== '' &&
          !this.systemConfiguration.logo_url.includes('/logo_flowit.png')) {
        let logoUrl = this.systemConfiguration.logo_url;

        // Para URLs externas (S3, Stackhero, etc.), adicionar timestamp para cache busting
        if (logoUrl.includes('http') && (logoUrl.includes('s3.amazonaws.com') || logoUrl.includes('stackhero'))) {
          const separator = logoUrl.includes('?') ? '&' : '?';
          logoUrl = `${logoUrl}${separator}cb=${this.logoUpdateTimestamp}`;
        }

        // Para URLs de API proxy, adicionar timestamp também
        if (logoUrl.includes('/api/')) {
          const separator = logoUrl.includes('?') ? '&' : '?';
          logoUrl = `${logoUrl}${separator}cb=${this.logoUpdateTimestamp}`;
        }

        return logoUrl;
      }

      // Fallback para logo da conta se disponível
      if (this.logoLinkFromStore && this.logoLinkFromStore.trim() !== '') {
        let logoUrl = this.logoLinkFromStore;

        // Adicionar cache busting para URLs externas
        if (logoUrl.includes('http') && (logoUrl.includes('s3.amazonaws.com') || logoUrl.includes('stackhero'))) {
          const separator = logoUrl.includes('?') ? '&' : '?';
          logoUrl = `${logoUrl}${separator}cb=${this.logoUpdateTimestamp}`;
        }

        return logoUrl;
      }

      // Último fallback para logo padrão
      return '/img/logo_flowit.png';
    },
    displayName() {
      return this.account.fantasy_name || this.account.name || 'Conta';
    },
    partnerTypeLabel() {
      const typeMap = {
        'franchisor': 'Franqueadora',
        'master': 'Master',
        'executive': 'Executiva',
        'establishment': 'Estabelecimento'
      };
      return typeMap[this.partner] || 'N/A';
    },
  },
  data() {
    return {
      isMobile: false,
      newAccount: {},
      logoUpdateTimestamp: Date.now(), // Para forçar atualização da logo
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
    };
  },
  watch: {
    account(newValues) {
      this.newAccount = newValues;
    },
    color: {
      handler(newColor, oldColor) {
        // Watcher para mudanças de cor
      },
      immediate: true
    },
    accountColor(newVal) {
      // Watcher para mudanças na cor da conta
    },
    systemPrimaryColor(newVal) {
      // Watcher para mudanças na cor primária do sistema
    }
  },
  methods: {
    onResize() {
      if (window.innerWidth < 769) this.isMobile = true;
      else this.isMobile = false;
    },
    showChangeForm() {
      EventBus.$emit("show-change-form");
    },
    showChangePasswordForm() {
      EventBus.$emit("show-change-password-form");
    },
    showChangeLogoForm() {
      this.$store.dispatch("account/showLogoForm", true);
    },
    showChangeFaviconForm() {
      this.$store.dispatch("account/showFaviconForm", true);
    },
    showChangeColorForm() {
      this.$store.dispatch("account/showColorForm", true);
    },
    onImageError() {
      // Se o erro foi em uma URL externa, tentar forçar o timestamp para cache busting
      const currentUrl = this.logoLink;
      if (currentUrl && currentUrl.includes('http') && !currentUrl.includes('cb=')) {
        this.logoUpdateTimestamp = Date.now();
        return; // Permite que o computed logoLink se atualize com timestamp
      }

      // Se já tem timestamp ou é uma URL local, usar logo padrão
      this.$store.commit('account/LOGO_LINK', '/img/logo_flowit.png');
    },
    copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.showSnackbar('Copiado para a área de transferência!', 'success');
        }).catch(() => {
          this.fallbackCopyToClipboard(text);
        });
      } else {
        this.fallbackCopyToClipboard(text);
      }
    },
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.showSnackbar('Copiado para a área de transferência!', 'success');
      } catch (err) {
        this.showSnackbar('Erro ao copiar. Tente novamente.', 'error');
      }
      textArea.remove();
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    onLogoUpdated() {
      // Atualizar timestamp para forçar re-computation do logoLink
      this.logoUpdateTimestamp = Date.now();
    },
    sendEmail() {
      // Obter domínio da variável de ambiente ou usar padrão
      const emailDomain = process.env.VUE_APP_EMAIL_DOMAIN || 'flowit.com.br';
      const emailAddress = `contato@${emailDomain}`;
      const subject = encodeURIComponent('Contato - Suporte');
      const body = encodeURIComponent(`Olá,\n\nPreciso de ajuda com:\n\n\n\nAtenciosamente,\n${this.account.name || ''}`);

      // Abrir cliente de email padrão do sistema operacional
      window.location.href = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    },
    getBalance() {
      return this.balance || 0;
    },
  },
  components: {
    ChangePassword: () =>
      import("@/components/desktop/my_account/account/NewPasswordForm.vue"),
    ChangeLogo: () =>
      import("@/components/desktop/my_account/account/LogoForm.vue"),
    ChangeFavicon: () =>
      import("@/components/desktop/my_account/account/FaviconForm.vue"),
    ChangeColor: () =>
      import("@/components/desktop/my_account/account/ColorForm.vue"),
    ChangeData: () =>
      import("@/components/desktop/my_account/account/EditForm.vue"),
  },
};
</script>

<style scoped>
.profile {
  max-width: 900px;
  margin: 0 auto;
  padding: 16px 12px 40px;
  overflow-x: hidden;
}

/* Hero */
.profile-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  border-radius: 22px;
  padding: 24px 26px;
  color: #ffffff;
  box-shadow: 0 14px 36px rgba(20, 30, 60, 0.18);
}
.profile-hero-id {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.profile-avatar {
  background: rgba(255, 255, 255, 0.9);
}
.profile-name {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}
.profile-email {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 2px;
  word-break: break-all;
}
.profile-edit {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
  margin: 0 !important;
}

/* Info grid */
.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
  margin-top: 18px;
}
.info-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 6px 22px rgba(20, 30, 60, 0.07);
  min-width: 0;
}
.info-tile--wide {
  grid-column: 1 / -1;
}
.info-tile-icon {
  flex-shrink: 0;
}
.info-tile-label {
  font-size: 12px;
  color: #8a94a6;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.info-tile-value {
  font-size: 15px;
  color: #1f2733;
  font-weight: 700;
  margin-top: 2px;
  word-break: break-word;
}

/* Sections + actions */
.profile-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2733;
  margin: 26px 4px 12px;
}
.profile-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.action-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  padding: 16px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2733;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;
  box-shadow: 0 4px 16px rgba(20, 30, 60, 0.05);
}
.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(20, 30, 60, 0.12);
}

/* Skeleton */
.profile-hero--sk {
  background: #e9edf3;
  box-shadow: none;
}
.sk {
  background: linear-gradient(90deg, #dfe4ec 25%, #eef1f6 50%, #dfe4ec 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 8px;
}
.sk-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
}
.sk-line {
  height: 14px;
  border-radius: 6px;
}
.sk-line-lg {
  width: 55%;
}
.sk-line-sm {
  width: 35%;
  margin-top: 10px;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
